// PermutationWithRepetitionTask.js
// Permutacje z powtórzeniami — liczymy n! / (n1! n2! ...)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

// prosta silnia
function fact(k) {
  let r = 1;
  for (let i = 2; i <= k; i++) r *= i;
  return r;
}

// losujemy liczności powtórzeń tak, aby co najmniej DWIE grupy miały powtórzenia
// przykład: A×a, B×b, C×c, (opcjonalnie D×d=1) i łącznie 6..10 znaków
export function generateTask() {
  // a ∈ {2,3}, b ∈ {2,3}, c ∈ {1,2,3}, opcjonalnie d = 1 (czasem dodamy literę bez powtórzeń)
  const a = 2 + Math.floor(Math.random() * 2); // 2..3
  const b = 2 + Math.floor(Math.random() * 2); // 2..3
  const c = 1 + Math.floor(Math.random() * 3); // 1..3

  // czasem dorzucimy jedną dodatkową literę bez powtórzeń, by zróżnicować n
  const addSingle = Math.random() < 0.5; // 50%
  const d = addSingle ? 1 : 0;

  const counts = [a, b, c].concat(d ? [d] : []);
  const letters = ["A", "B", "C"].concat(d ? ["D"] : []);

  const n = counts.reduce((s, x) => s + x, 0); // 6..10
  const numerator = fact(n);
  const denominator = counts.map(fact).reduce((p, x) => p * x, 1);
  const answer = Math.floor(numerator / denominator);

  return {
    type: "PermutationWithRepetitionTask",
    data: { letters, counts, n, numerator, denominator, answer },
  };
}

export function renderTask({ data }) {
  const { letters, counts, n } = data;

  return (
    <div style={{ textAlign: "left" }}>
      <p>
        Ile różnych <strong>kodów</strong> długości <InlineMath math={`${n}`} /> można
        utworzyć z następujących liter (kolejność liter w kodzie ma znaczenie,
        ale litery powtarzające się są nieodróżnialne)?
      </p>
      <ul>
        {letters.map((L, i) => (
          <li key={L}>
            Litera <strong>{L}</strong>: <InlineMath math={`${counts[i]}`} /> razy
          </li>
        ))}
      </ul>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="" // bez prefiksu w podglądzie
      placeholder="Wpisz liczbę układów"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") {
    return { correct: false, message: "Podaj liczbę." };
  }
  const given = parseInt(userAnswer.raw, 10);
  if (Number.isNaN(given)) {
    return { correct: false, message: "Podaj liczbę całkowitą." };
  }
  const correct = given === data.answer;
  userAnswer.correctAnswer = data.answer;
  return { correct, message: correct ? "Dobrze!" : "Spróbuj ponownie." };
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.answer}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`${userAnswer.raw}`} />;
}

export function renderExplanation({ data }) {
  const { letters, counts, n, numerator, denominator, answer } = data;

  // latex do mianownika
  const denomLatex = counts.map((x) => `${x}!`).join(" \\cdot ");
  const multisetLatex = letters
    .map((L, i) => `${L}^{\\times ${counts[i]}}`)
    .join(",\\; ");

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1. Rozpoznajemy litery.</strong> Mamy zbiór znaków (z powtórzeniami):
        <InlineMath math={`\\{${multisetLatex}\\}`} />, razem{" "}
        <InlineMath math={`${n}`} /> liter.
      </p>

      <p>
        <strong>Krok 2. Gdyby wszystkie litery były różne,</strong> można by je ułożyć na{" "}
        <InlineMath math={`${n}!`} /> sposobów.
      </p>

      <p>
        <strong>Krok 3. Dlaczego trzeba dzielić?</strong> Jeśli litery się powtarzają,
        to zamiana ich miejsc między sobą nie tworzy nowego kodu.
        Dlatego „nadliczbowe” uło­żenia skracamy przez{" "}
        <InlineMath math={denomLatex} />, czyli silnie z liczności poszczególnych liter.
      </p>

      <p>
        <strong>Krok 4. Wzór końcowy.</strong>
      </p>
      <BlockMath math={`\\dfrac{${n}!}{${denomLatex}}`} />

      <p>
        <strong>Krok 5. Podstawienie i obliczenia.</strong>
      </p>
      <BlockMath math={`\\dfrac{${numerator}}{${denominator}} = ${answer}`} />
    </div>
  );
}